import extractorError from '@danwang/extractor/lib/error';
import Money from 'common/Money';
import {Loader2} from 'lucide-react';
import * as React from 'react';
import {AmountDisplay} from 'src/AmountDisplay';
import {cn} from 'src/lib/utils';
import type {ExtractionError} from '@danwang/extractor/lib/error';

export type ImportPreviewRow = {
  id: string;
  date: string;
  description: string;
  /** Signed USD cents, as the row will be imported. */
  amount: number;
};

type ListProps = {
  rows: ImportPreviewRow[];
  /**
   * Row ids that already exist server-side. They render dimmed with an
   * "imported" badge — the server skips them on import, so the summary and
   * total only count the rest. Omit entirely when the source has no
   * duplicate check (Venmo): the server still dedupes on import, so the
   * header then counts plain "transactions" rather than promising "new"
   * ones it cannot verify.
   */
  existingIds?: ReadonlySet<string>;
  /** True while the duplicate check is in flight. */
  checkingExisting?: boolean;
};

export const ImportPreviewList = ({
  rows,
  existingIds,
  checkingExisting = false,
}: ListProps) => {
  if (rows.length === 0) {
    return (
      <div className="text-muted-foreground text-sm">
        No transactions found in the pasted data.
      </div>
    );
  }

  const newRows = existingIds
    ? rows.filter((row) => !existingIds.has(row.id))
    : rows;
  const existingCount = rows.length - newRows.length;
  const newTotal = newRows.reduce((sum, row) => sum + row.amount, 0);
  const countNoun = newRows.length === 1 ? 'transaction' : 'transactions';

  return (
    <div className="border-border rounded-md border">
      <div className="border-border bg-muted/50 flex items-center justify-between gap-3 rounded-t-md border-b px-3 py-2 text-sm">
        {checkingExisting ? (
          <span className="text-muted-foreground flex items-center gap-1.5">
            <Loader2 className="size-3.5 animate-spin" />
            Checking for duplicates…
          </span>
        ) : (
          <span className="text-muted-foreground">
            <span className="text-foreground">
              {newRows.length} {existingIds ? `new ${countNoun}` : countNoun}
            </span>
            {existingCount > 0 && <>, {existingCount} already imported</>}
          </span>
        )}
        <AmountDisplay amount={Money.fromInt(newTotal, 'USD')} variant="plain" />
      </div>
      <div className="max-h-72 overflow-y-auto">
        {rows.map((row) => {
          const isExisting = existingIds?.has(row.id) ?? false;
          return (
            <div
              key={row.id}
              className={cn(
                'not-last:border-border/50 flex items-center gap-3 px-3 py-2 not-last:border-b',
                isExisting && 'opacity-40',
              )}
            >
              <div className="text-muted-foreground w-20 shrink-0 text-xs tabular-nums">
                {row.date}
              </div>
              <div
                className="text-foreground min-w-0 flex-1 truncate text-sm"
                title={row.description}
              >
                {row.description}
              </div>
              {isExisting && (
                <span className="bg-muted text-muted-foreground shrink-0 rounded-full px-2 py-0.5 text-[0.7rem]">
                  imported
                </span>
              )}
              <div className="shrink-0 text-sm">
                <AmountDisplay amount={Money.fromInt(row.amount, 'USD')} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MAX_ERROR_LINES = 3;
const MAX_ERROR_LINE_LENGTH = 200;

// formatError embeds the offending value verbatim ("…got <entire paste>"), so
// lines must be truncated or a failed paste renders itself back as a wall of
// text.
const truncateLine = (line: string): string =>
  line.length > MAX_ERROR_LINE_LENGTH
    ? `${line.slice(0, MAX_ERROR_LINE_LENGTH)}…`
    : line;

type ErrorProps = {
  /** Names the expected shape in the headline, e.g. "E-ZPass". */
  sourceName: string;
  error: ExtractionError;
};

export const ImportParseError = ({sourceName, error}: ErrorProps) => {
  const lines = extractorError.formatError(extractorError.simplifyError(error));
  const shown = lines.slice(0, MAX_ERROR_LINES);
  const hiddenCount = lines.length - shown.length;

  return (
    <div className="border-destructive/50 rounded-md border px-3 py-2 text-sm">
      <div className="text-destructive">
        Could not parse this as {sourceName} JSON.
      </div>
      <div className="text-muted-foreground mt-1 flex flex-col gap-0.5 font-mono text-xs">
        {shown.map((line, i) => (
          <div key={i} className="break-all">
            {truncateLine(line)}
          </div>
        ))}
        {hiddenCount > 0 && <div>…and {hiddenCount} more</div>}
      </div>
    </div>
  );
};
